import { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty, trimEnd, values, trim, flattenDeep } from 'lodash';
import CreditCard from '@components/CreditCard';
import Error from '@components/Error';
import { Context } from '@entities/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faCheckCircle, faCircleNotch, faExclamationTriangle, faReply } from '@fortawesome/free-solid-svg-icons';

const Source = () => {
    const { api, states, storage } = useContext(Context)
    const navigate = useNavigate();
    const cardRef = useRef();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [formData, setFormData] = useState({
        description: '',
        name: '',
        address1: '',
        address2: null,
        city: '',
        state: 'TX',
        zipcode: '',
        country: 'USA',
        currency: 'usd'
    });

    useEffect(() => {
        const pin = storage.get('pin');
        if (!pin) {
            navigate('/login', { replace: true });
            return;
        }

        setFormData(prev => ({
            ...prev,
            name: storage.get('account').name
        }));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'zipcode') {
            const zipcode = value.replace(/[^0-9]/g, '');
            const base = zipcode.substr(0, 5);
            const extension = zipcode.substr(5, 4);
            setFormData(prev => ({
                ...prev,
                zipcode: trimEnd(base + '-' + extension, '-')
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const isDisabled = () => {
        return loading
            || isEmpty(formData.description)
            || isEmpty(formData.name)
            || isEmpty(formData.address1)
            || isEmpty(formData.city)
            || isEmpty(formData.state)
            || isEmpty(formData.zipcode)
            || isInvalidZipcode();
    };

    const isInvalidZipcode = () => {
        return !isEmpty(formData.zipcode)
            && !/^\d{5}$|^\d{9}$|^\d{5}-\d{4}$/.test(formData.zipcode);
    };

    const handleBack = () => {
        navigate('/dashboard');
    };

    const handleSave = async () => {
        if (loading) return;
        setLoading(true);
        setError(false);

        try {
            const { token } = await cardRef.current.submit({
                name: formData.name,
                address1: formData.address1,
                address2: formData.address2,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                zipcode: formData.zipcode,
                currency: formData.currency,
            });

            const { data } = await api.post('/api/source', {
                account: storage.get('account').id,
                pin: storage.get('pin'),
                type: 'card',
                description: formData.description,
                token: token,
            });

            let sources = storage.get('sources');
            sources = values(sources.concat(data.data));
            storage.set('sources', sources);
            setLoading(false);
            handleBack();
        } catch (error) {
            setLoading(false);
            if (error.response) {
                setError(trim(
                    error.response.data.message
                    + ' ' + flattenDeep(values(error.response.data.errors || {})).join(' ')
                ));
            }
        }
    };

    return (
        <div>
            <p className="mb-6 text-lg font-light">
                You'll need to add a contribution source to make your online contributions. You may add a
                credit or debit card to your account. You can help save the church credit card
                processing fees by adding a bank account and making contributions using that,
                much like an e-check.
            </p>
            {error && <Error message={error} />}
            <div className="flex flex-wrap -mx-4">
                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 font-bold text-left text-gray-700 required" htmlFor="description">
                        Account Description
                    </label>
                    <input
                        value={formData.description}
                        onChange={handleChange}
                        type="text"
                        name="description"
                        maxLength="32"
                        id="description"
                        className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                        Choose a common name to identify this account.
                    </p>
                </div>
                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 font-bold text-left text-gray-700 required" htmlFor="name">
                        Account Holder
                    </label>
                    <input
                        value={formData.name}
                        onChange={handleChange}
                        type="text"
                        name="name"
                        id="name"
                        className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                </div>

                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 font-bold text-left text-gray-700 required" htmlFor="address">
                        Card Info 
                    </label>
                    <div id="card" className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                    <CreditCard className="w-full px-4 mb-4" ref={cardRef}>
                    {({ error: cardError }) => (
                        <>
                            {cardError && (
                                <div id="errors" role="alert" className="py-2 text-sm text-red-700">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="fas" />
                                    {cardError}
                                </div>
                            )}
                        </>
                    )}
                    </CreditCard>
                    </div>
                </div>


                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 font-bold text-left text-gray-700 required" htmlFor="address">
                        Billing Address
                    </label>
                    <input
                        value={formData.address1}
                        onChange={handleChange}
                        type="text"
                        name="address1"
                        id="address"
                        className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                </div>
                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 font-bold text-left text-gray-700 required" htmlFor="city">
                        City
                    </label>
                    <input
                        value={formData.city}
                        onChange={handleChange}
                        type="text"
                        name="city"
                        id="city"
                        className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    />
                </div>
                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 font-bold text-left text-gray-700 required" htmlFor="state">
                        State
                    </label>
                    <div className="relative">
                        <select
                            value={formData.state}
                            onChange={handleChange}
                            name="state"
                            className="w-full px-3 py-3 leading-tight text-gray-700 bg-white border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            id="source"
                            required
                        >
                            {Object.entries(states).map(([abbreviation, name]) => (
                                <option key={abbreviation} value={abbreviation}>{name}</option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
                            <FontAwesomeIcon icon={faAngleDown} className="mr-2 fas" /> 
                        </div>
                    </div>
                </div>
                <div className="w-full px-4 mb-4 sm:w-1/2">
                    <label className="block mb-2 text-sm font-bold required" htmlFor="zipcode">
                        ZIP Code
                    </label>
                    <input
                        value={formData.zipcode}
                        onChange={handleChange}
                        type="text"
                        name="zipcode"
                        id="zipcode"
                        className={`shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            isInvalidZipcode() ? 'border-red-500 bg-red-100' : ''
                        }`}
                        inputMode="numeric"
                    />
                </div>
                <div className="w-full px-4 mt-4">
                    <div className="flex flex-wrap justify-between">
                        <button
                            onClick={handleSave}
                            disabled={isDisabled()}
                            className="px-6 py-3 mb-4 font-bold text-white rounded bg-primary disabled:bg-blue-400 disabled:cursor-not-allowed"
                            type="button"
                        >
                            {loading ? (
                                <span>
                                    <FontAwesomeIcon icon={faCircleNotch} className="fas fa-lg animate-spin"/>
                                </span>
                            ) : (
                                <span>
                                    <FontAwesomeIcon icon={faCheckCircle} className="fas fa-lg"/>
                                </span>
                            )}
                            <span className="inline-block ml-3">
                                {loading ? 'Saving...' : 'Save Card'}
                            </span>
                        </button>
                        <button onClick={handleBack} className="text-gray-500 truncate hover:text-primary">
                            <FontAwesomeIcon icon={faReply} className="mr-2 fas"/>
                            Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Source;
